import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';

import logEvent from '@zr/utilities/logEvent';
import { GENERIC_ERROR_MESSAGE } from '@zr/utilities/constants';
import { fetchLocationResults } from '@zr/utilities/api/locationField';
import { patchProfile } from '@zr/utilities/api/profile';
import buildProfileLocationString from '@zr/utilities/buildProfileLocationString';
import LabeledField from '@zr/components/Fields/LabeledField';

import PartnerRequiredLocationField from './PartnerRequiredLocationField';
import { LOCALE_DATA, SUGGESTED_JOBS_URL, REQUIRED_FIELDS } from '../constants';

import './PartnerRequiredFields.less';

/**
 * A form that collects jobseeker country, ZIP / postal code, city, and state.
 * All fields are required. Jobseeker selects country from dropdown, then uses
 * a geoAC-based autocomplete to select a ZIP code. The autocomplete field
 * populates hidden values for the other required fields based on the geoAC
 * results. If country selected is "Other", we do not allow form submission.
 */

// jsx elements
const otherCountryErrorMessage = (
  <>
    {'This Employer is not accepting non-US/CA applicants at this time. Please click '}
    <a href={`${SUGGESTED_JOBS_URL}#instrc=fe.partner_required_fields.suggested_jobs_link_other_country`}>here</a>
    {' to see suggested jobs that may be more relevant based on your location.'}
  </>
);

const profileWarningTooltip = (
  <i
    className="icon_info"
    data-callout="tooltip"
    title="You can remove this information by editing your profile."
    role="tooltip"
  />
);

const PartnerRequiredFields = ({
  onDone,
  profileCity,
  profileState,
  profileCountry,
  profileZipCode,
  locationAutocompleteUrl,
  reattemptRequiredFields,
  setIsLoading,
}) => {
  // keep track of whether the value of the ZIP / Postal code field was set by
  // the user selecting from the autocomplete. false if the user has edited the
  // field through means other than selecting a location from the autocomplete
  const [locationClean, setLocationClean] = useState(false);

  // set up location string based on current info in jobseeker's profile and
  // determine whether it's incomplete/invalid/etc.
  const locationFields = [];

  if (profileCity) {
    locationFields.push(`${profileCity},`);
  }

  if (profileState) {
    locationFields.push(profileState);
  }

  if (profileZipCode) {
    locationFields.push(profileZipCode);
  }

  // this gets displayed in the ZIP / Postal code field and should not include the country
  const currentLocation = locationFields.join(' ');

  if (profileCountry) {
    locationFields.push(profileCountry);
  }

  const numberOfCompletedFields = locationFields.length;

  let currentAddressStatus;

  if (reattemptRequiredFields) {
    // if they are attempting this page for the second or more time
    currentAddressStatus = 'reattempt';
  } else if (numberOfCompletedFields === 0) {
    // if we have 0 address fields, then we do not have any location
    currentAddressStatus = 'missing';
  } else if (numberOfCompletedFields < 4) {
    // if we have 1-4 fields, then we know the address is incomplete
    currentAddressStatus = 'incomplete';
  } else {
    // if we have all 4 required fields, then the address is invalid
    currentAddressStatus = 'invalid';
  }

  // the autocomplete URL changes based on which country is selected in the form
  const buildAutoCompleteUrl = useCallback(
    (country) => locationAutocompleteUrl.replace(/filter=[^&]*/, `filter=${LOCALE_DATA.geo_ac_filter[country]}`), [locationAutocompleteUrl],
  );

  // for an autocomplete result, true if item has all the required fields. false otherwise.
  const hasAllRequiredFields = (item) => REQUIRED_FIELDS.every((field) => !!item[field]);

  // set up some state for the form
  const [acUrl, setAcUrl] = useState('');
  const [initialCountryValue, setInitialCountryValue] = useState('');
  const [initialLocationValue, setInitialLocationValue] = useState('');
  const [forcedResults, setForcedResults] = useState([]);

  // fill form fields as needed
  useEffect(() => {
    async function preFillForm() {
      const profileCountryKey = profileCountry?.toLowerCase();
      const validProfileCountry = LOCALE_DATA.countries.indexOf(profileCountryKey) > -1;

      // pre-fill country field if profile has an accepted country
      if (validProfileCountry) {
        setInitialCountryValue(LOCALE_DATA.form_value[profileCountryKey]);

        const url = buildAutoCompleteUrl(profileCountryKey);
        setAcUrl(url);

        // if the profile has some location info that returns results, set them & pre-fill the location field
        if (currentLocation.length) {
          const results = await fetchLocationResults(url, currentLocation, 'p');
          const filteredResults = results.filter((result) => hasAllRequiredFields(result));

          if (filteredResults.length) {
            setForcedResults(filteredResults);
            setInitialLocationValue(currentLocation);
          }
        }
      }
    }
    preFillForm();
  }, [profileCountry, currentLocation, locationAutocompleteUrl, buildAutoCompleteUrl]);

  const countrySelectOptions = LOCALE_DATA.countries.map((country) => (
    <option key={country} value={LOCALE_DATA.form_value[country]}>{LOCALE_DATA.form_label[country]}</option>
  ));

  // handle form submission
  const handleFormSubmit = useCallback(async (values, { setSubmitting, setStatus, setFieldError }) => {
    if (values.display_country === LOCALE_DATA.form_value.other) {
      // do not allow form submission if "Other" country is selected
      setStatus({ error: otherCountryErrorMessage });
      setSubmitting(false);
    } else if (locationClean && hasAllRequiredFields(values) && values.location) {
      // they made a selection from the autocomplete and have not edited it, and we have all the required fields
      // map form values to be profile compatible
      const newValues = {
        city: values.city,
        state: values.admin1_code,
        zip_code: values.postal_code,
        country: values.cc,
        location: values.location,
        profile_method: 'fe.partner_required_fields.update',
      };

      setStatus({ error: '' }); // clear any old error messages

      try {
        // `setIsLoading(true)` so that we can disable the close button while the
        // screen is processing/loading
        setIsLoading(true);
        await patchProfile(newValues);

        // if successful submit, set form status so we can disable the input
        // fields while we wait for the redirect
        setStatus({});
        // onDone could be async, go to catch block if it errors
        await onDone();
      } catch (error) {
        setSubmitting(false);
        setStatus({ error: GENERIC_ERROR_MESSAGE });
      }
    } else {
      // they typed in a freeform response, or edited the field after
      // having selected a location from the autocomplete
      setStatus({ error: 'Please begin typing below and select a location from the list of results.' });
      setFieldError('display_location', LOCALE_DATA.zip_code_label[values.display_country]);
      setSubmitting(false);
    }
  }, [locationClean, onDone, setIsLoading]);

  // log country selection only once
  const refLoggedCountry = useRef(false);

  const logCountryOnce = (value) => {
    if (!refLoggedCountry.current) {
      logEvent('partner_required_fields_collection_data', { field: 'country', value });
      refLoggedCountry.current = true;
    }
  };

  return (
    <section className="PartnerRequiredFields">
      {currentAddressStatus === 'missing' && (
        <>
          <h1 className="headline">Please confirm your location in order to submit your application.</h1>
          <p className="profile_warning">
            This information will be added to your profile.
            {profileWarningTooltip}
          </p>
        </>
      )}
      {currentAddressStatus === 'incomplete' && (
        <>
          <h1 className="headline">Additional information is required to complete your application.</h1>
          <p className="profile_warning">
            Please provide your ZIP / Postal code. This information will be added to your profile.
            {profileWarningTooltip}
          </p>
        </>
      )}
      {currentAddressStatus === 'invalid' && (
        <>
          <h1 className="headline">We were unable to validate your location.</h1>
          <p className="profile_warning">
            To complete your application please provide your ZIP / Postal code.
            This information will be added to your profile.
            {profileWarningTooltip}
          </p>
        </>
      )}
      {currentAddressStatus === 'reattempt' && (
        <>
          <h1 className="headline">There was an issue validating the location you selected, please try again.</h1>
          <p className="profile_warning">
            This information will be added to your profile.
            {profileWarningTooltip}
          </p>
        </>
      )}

      <Formik
        initialValues={{
          display_country: initialCountryValue,
          display_location: initialLocationValue,
          city: '',
          admin1_code: '',
          postal_code: '',
          cc: '',
          location: '', // profile patch requires a location to be sent
        }}
        enableReinitialize // we are setting some initial values based on state
        onSubmit={handleFormSubmit}
        validationSchema={Yup.object().shape({
          display_location: Yup.string()
            // display_location (ZIP / Postal code field) required if country is anything other than 'other'
            .when('display_country', {
              is: (val) => val === LOCALE_DATA.form_value.us,
              then: (val) => val.required(`Please enter a valid ${LOCALE_DATA.zip_code_label.us}`),
            })
            .when('display_country', {
              is: (val) => val === LOCALE_DATA.form_value.ca,
              then: (val) => val.required(`Please enter a valid ${LOCALE_DATA.zip_code_label.ca}`),
            }),
          display_country: Yup.string().required('Please select a Country'),
        })}
      >
        {({
          values, errors, touched, isSubmitting, handleSubmit, handleChange, status, setFieldValue,
        }) => {
          // handle selection of location from autocomplete field
          const handleLocationSelect = (value, item) => {
            setFieldValue('display_location', value);
            setFieldValue('city', item.city);
            setFieldValue('admin1_code', item.admin1_code);
            setFieldValue('cc', item.cc);
            setFieldValue('postal_code', item.postal_code);
            setFieldValue('location', buildProfileLocationString({ country: item.cc, city: item.city, state: item.admin1_code }));

            setLocationClean(true);
          };

          // mark field as touched when user types into it directly. must be clean for submission.
          const handleLocationChange = (e) => {
            setLocationClean(false);
            handleChange(e);
          };

          const handleCountryChange = (e) => {
            const country = e.target.value;

            // update autocomplete URL if a valid country
            if (LOCALE_DATA.countries.indexOf(country) > -1) {
              const countrySpecificUrl = buildAutoCompleteUrl(country);
              setAcUrl(countrySpecificUrl);
            }

            setFieldValue('display_location', ''); // clear the ZIP / Postal code field
            setForcedResults([]); // clear the ZIP / Postal code menu dropdown
            setLocationClean(false);
            handleChange(e);
            logCountryOnce(e.target.value);
          };

          return (
            <>
              {status?.error && (
                <div className="zrs_message_danger not_dismissable has_icon error_message" role="alert">
                  <p>{status.error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate className="location_form">
                <fieldset>
                  <legend className="location_info_legend">Location Information</legend>
                  {/* Country select dropdown. For UI only. We do not store data from this field. */}
                  <LabeledField
                    as="select"
                    id="display_country"
                    onChange={handleCountryChange}
                    label="Country"
                    placeholder="Country"
                    input={{ name: 'display_country', value: values.display_country, disabled: isSubmitting }}
                    meta={{ error: errors.display_country, touched: touched.display_country }}
                  >
                    {countrySelectOptions}
                  </LabeledField>
                  {/* ZIP / Postal code field. Shown if a country other than "Other" is selected.
                  When this field is updated, we set the hidden fields that  capture the required
                  information (city, state, zip, country ) */}
                  {values.display_country && values.display_country !== LOCALE_DATA.form_value.other && (
                    <LabeledField
                      as={PartnerRequiredLocationField}
                      autoFocus
                      input={{ name: 'display_location', value: values.display_location, maxLength: 128, disabled: isSubmitting }}
                      meta={{ error: errors.display_location, touched: touched.display_location }}
                      label={LOCALE_DATA.zip_code_label[values.display_country]}
                      placeholder={LOCALE_DATA.zip_code_label[values.display_country]}
                      locationAutocompleteUrl={acUrl}
                      onChange={handleLocationChange}
                      onSelect={handleLocationSelect}
                      shouldItemRender={hasAllRequiredFields} // let the autocomplete know which results to render
                      forcedResults={forcedResults} // sets autocomplete menu results
                    />
                  )}
                </fieldset>
                <div className="submit_button_wrapper">
                  <button type="submit" className="btn" disabled={isSubmitting}>
                    <span className="btn_txt">Continue</span>
                  </button>
                </div>
              </form>

              <p className="hint">
                {'If you\'re not able to find your exact location, ensure the ZIP / Postal code is accurate and select the city closest to you. If you\'re still not able to find an accurate location, click '}
                <a href={`${SUGGESTED_JOBS_URL}#instrc=fe.partner_required_fields.suggested_jobs_link`}>here</a>
                {' to see suggested jobs that may be more relevant based on your location.'}
              </p>
            </>
          );
        }}
      </Formik>
    </section>
  );
};

PartnerRequiredFields.propTypes = {
  onDone: PropTypes.func.isRequired,
  profileCity: PropTypes.string,
  profileState: PropTypes.string,
  profileCountry: PropTypes.string,
  profileZipCode: PropTypes.string,
  locationAutocompleteUrl: PropTypes.string.isRequired,
  reattemptRequiredFields: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func,
};

PartnerRequiredFields.defaultProps = {
  profileCity: '',
  profileState: '',
  profileCountry: '',
  profileZipCode: '',
  setIsLoading: () => {},
};

export default PartnerRequiredFields;
